import { Box, Table, Text } from '@/components';
import type { UpdatedProjectSummaryModel } from '@/features/project/projects/_models/project';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  summary: z.infer<typeof UpdatedProjectSummaryModel>;
};

type TableDataSource = {
  key: string;
  title: string;
  initialBudget: number | null;
  nextYearRequested: number | null;
  previousYearExecuted: number | null;
};

export const BudgetVsRequested: FC<Props> = ({ summary }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const getRenderedValue = (
    value: number,
    record: TableDataSource,
    operationType?: 'round' | 'floor' | 'ceil',
  ) => {
    const differenceValue = formatValueByUnit(value, {
      operation: operationType ?? 'round',
      unit,
    });

    return record.key === 'totalAmount' ? (
      <>
        <Text variant="h4" text={differenceValue} />
        <Text
          variant="footnote"
          color="secondary"
          text={t('thousand-yen-unit')}
        />
      </>
    ) : (
      <>
        <Text variant="h5" weight="bold" text={differenceValue} />
        <Text
          variant="footnote"
          color="secondary"
          text={t('thousand-yen-unit')}
        />
      </>
    );
  };

  return (
    <Box marginBottom={16}>
      <Table
        dataSource={[
          {
            key: 'generalAccount',
            title: t('general-account'),
            initialBudget:
              summary.finalizedAmountForGeneralAccountAndInitialBudget,
            nextYearRequested: summary.requestedAmountForGeneralAccount,
            previousYearExecuted:
              summary.previousYearExecutedAmountForGeneralAccount,
          },
          {
            key: 'specialAccount',
            title: t('special-account'),
            initialBudget:
              summary.finalizedAmountForSpecialAccountAndInitialBudget,
            nextYearRequested: summary.requestedAmountForSpecialAccount,
            previousYearExecuted:
              summary.previousYearExecutedAmountForSpecialAccount,
          },
          {
            key: 'totalAmount',
            title: t('total'),
            initialBudget: summary.finalizedAmountForInitialBudget,
            nextYearRequested: summary.requestedAmount,
            previousYearExecuted: summary.previousYearExecutedAmount,
          },
        ]}
        columns={[
          {
            title: '',
            key: 'title',
            dataIndex: 'title',
          },
          {
            title: t('previous-fiscal-year-executed'),
            key: 'previousYearExecuted',
            dataIndex: 'previousYearExecuted',
            align: 'right',
            render: (value, record) => getRenderedValue(value, record, 'floor'),
          },
          {
            title: t('initial-finalized-amount'),
            key: 'initialBudget',
            dataIndex: 'initialBudget',
            align: 'right',
            render: (value, record) => getRenderedValue(value, record),
          },
          {
            title: t('next-request-amount'),
            key: 'nextYearRequested',
            dataIndex: 'nextYearRequested',
            align: 'right',
            render: (value, record) => getRenderedValue(value, record),
          },
        ]}
        pagination={false}
      />
    </Box>
  );
};
